import { useEffect, useState } from 'react'
import { useIntegrationApp } from '@integration-app/react'
import {
  DataSchema,
  IntegrationAppClient,
  Workspace,
} from '@integration-app/sdk'
import { TbBook } from 'react-icons/tb'

import {
  CodeParams,
  CodeBlockRunButton,
  LoginToRun,
  CodeLanguageSelector,
} from 'components/CodeBlock/components'
import { CodeBlockElement } from 'components/CodeBlock/elements'
import { ErrorsBadge } from 'components/Error/ErrorsBadge'
import { WorkspaceRelatedLink } from 'components/Link/WorkspaceRelatedLink'
import useAuth from 'contexts/auth'
import { SETTINGS_ROUTE } from 'routes/Workspaces/Workspace/routes-constants'
import { HStack } from 'styled-system/jsx'
import { Text } from 'ui-kit/text'

import useDocs from '../../routes/Docs/components/docs-context'
import { CodeHighlighterLanguagesEnum } from '../CodeHighlighter'
import {
  CodeBlockExtension,
  CodeBlockLanguageTitle,
  CodeBlockType,
  CodeExampleType,
  CodeParamType,
} from './codeBlock-types'
import DocLink from './DocLink'
import { useWorkspace } from '../Workspaces/workspace-context'

const CodeBlockExtensions = new Map<
  CodeBlockExtension,
  {
    title: CodeBlockLanguageTitle
    language: CodeHighlighterLanguagesEnum
  }
>()
CodeBlockExtensions.set('js', {
  title: CodeBlockLanguageTitle.Javascript,
  language: CodeHighlighterLanguagesEnum.javascript,
})
CodeBlockExtensions.set('ts', {
  title: CodeBlockLanguageTitle.Javascript,
  language: CodeHighlighterLanguagesEnum.typescript,
})
CodeBlockExtensions.set('tsx', {
  title: CodeBlockLanguageTitle.React,
  language: CodeHighlighterLanguagesEnum.tsx,
})
CodeBlockExtensions.set('jsx', {
  title: CodeBlockLanguageTitle.React,
  language: CodeHighlighterLanguagesEnum.jsx,
})
CodeBlockExtensions.set('vue', {
  title: CodeBlockLanguageTitle.Vue,
  language: CodeHighlighterLanguagesEnum.jsx,
})
CodeBlockExtensions.set('http', {
  title: CodeBlockLanguageTitle.API,
  language: CodeHighlighterLanguagesEnum.http,
})
CodeBlockExtensions.set('curl', {
  title: CodeBlockLanguageTitle.curl,
  language: CodeHighlighterLanguagesEnum.bash,
})
CodeBlockExtensions.set('sh', {
  title: CodeBlockLanguageTitle.Bash,
  language: CodeHighlighterLanguagesEnum.bash,
})
CodeBlockExtensions.set('npm', {
  title: CodeBlockLanguageTitle.npm,
  language: CodeHighlighterLanguagesEnum.bash,
})
CodeBlockExtensions.set('html', {
  title: CodeBlockLanguageTitle.html,
  language: CodeHighlighterLanguagesEnum.html,
})
CodeBlockExtensions.set('yarn', {
  title: CodeBlockLanguageTitle.yarn,
  language: CodeHighlighterLanguagesEnum.bash,
})
CodeBlockExtensions.set('go', {
  title: CodeBlockLanguageTitle.Go,
  language: CodeHighlighterLanguagesEnum.go,
})
CodeBlockExtensions.set('python', {
  title: CodeBlockLanguageTitle.Python,
  language: CodeHighlighterLanguagesEnum.python,
})
CodeBlockExtensions.set('java', {
  title: CodeBlockLanguageTitle.Java,
  language: CodeHighlighterLanguagesEnum.java,
})
CodeBlockExtensions.set('php', {
  title: CodeBlockLanguageTitle.PHP,
  language: CodeHighlighterLanguagesEnum.php,
})
CodeBlockExtensions.set('ruby', {
  title: CodeBlockLanguageTitle.Ruby,
  language: CodeHighlighterLanguagesEnum.ruby,
})

export interface RunArgs {
  integrationApp: IntegrationAppClient
  parameters: Record<string, any>
  workspace: Workspace
}

export interface ParameterSpec {
  type: CodeParamType
  title?: string
  schema?: DataSchema
  default?: any
  readOnly?: boolean
}

export type ParametersSpec = Record<string, CodeParamType | ParameterSpec>

const LANGUAGE_HELP_ARTICLES = {
  javascript: 'getting-started/front-end/javascript',
  jsx: 'getting-started/front-end/react',
  curl: 'getting-started/backend/rest-api',
}

export default function ExampleCodeBlock({
  variables = {},
  variablesSpec = {},
  customCodes,
  parameters: parametersSpec = {},
  showOutput = true,
  parametersExpandedByDefault = false,
  run,
  key,
}: {
  variables?: Record<string, string>
  variablesSpec?: ParametersSpec
  customCodes: CodeBlockType
  parameters?: ParametersSpec
  showOutput?: boolean
  key?: string
  parametersExpandedByDefault?: boolean
  run?: (args: RunArgs) => Promise<any>
}) {
  const { self, loginLink } = useAuth()
  const { workspace } = useWorkspace()
  const { parameters } = useDocs()
  const integrationApp = useIntegrationApp()

  const codeExamples = codeBlocksToCodeExamples(customCodes)

  const [output, setOutput] = useState(null)
  const [error, setError] = useState(null)
  const [running, setRunning] = useState(false)

  async function doRun() {
    if (!run) {
      return
    }

    setRunning(true)
    try {
      const result = await run({
        integrationApp,
        workspace,
        parameters,
      })
      setOutput(result ?? 'No output')
      setError(null)
    } catch (e) {
      setError(e)
    } finally {
      setRunning(false)
    }
  }

  function replaceParamsAndVariables(code: string) {
    return renderVariables(
      renderVariables(code, parameters, parametersSpec),
      variables,
      variablesSpec,
    )
  }

  useEffect(() => {
    setOutput(null)
    setError(null)
  }, [key])

  const [currentLanguage, setCurrentLanguage] = useState(
    codeExamples[0].language,
  )
  const currentCodeExample = codeExamples.find(
    (codeExample) => codeExample.language === currentLanguage,
  ) as CodeExampleType

  function onLanguageChange(value: CodeExampleType['language']) {
    setCurrentLanguage(value)
  }

  const helpArticle = LANGUAGE_HELP_ARTICLES[currentLanguage] ? (
    <DocLink
      path={LANGUAGE_HELP_ARTICLES[currentLanguage]}
      colorPalette='primary'
      rightIcon={<TbBook />}
    >
      How to run it in your app
    </DocLink>
  ) : undefined

  const runNotification = self?.user?.id ? (
    <Text as='span' maxWidth='sm' display='inline-block' lineHeight='tight'>
      This code will run on behalf of your{' '}
      <WorkspaceRelatedLink
        colorPalette='primary'
        to={`${SETTINGS_ROUTE}/testing`}
      >
        test customer
      </WorkspaceRelatedLink>
      .
    </Text>
  ) : undefined

  return (
    <CodeBlockElement.Root className={'mt-4 mb-6'}>
      <CodeParams
        expandedByDefault={parametersExpandedByDefault}
        disabled={!self?.user?.id}
        parametersSpec={parametersSpec}
      />

      <CodeBlockElement.Section
        className={'py-1.5 bg-neutral01 items-center'}
        rightSlot={helpArticle}
      >
        <CodeLanguageSelector
          value={currentLanguage}
          onChange={onLanguageChange}
          codeExamples={codeExamples}
        />
      </CodeBlockElement.Section>

      <CodeBlockElement.Code
        code={replaceParamsAndVariables(currentCodeExample.code).trim()}
        language={currentCodeExample.language}
        copyToClipboard
      />

      {run && (
        <CodeBlockElement.Section rightSlot={runNotification}>
          {self?.user?.id ? (
            <HStack>
              <CodeBlockRunButton
                onClick={doRun}
                disabled={running}
                loading={running}
              >
                {running ? 'Running...' : 'Run Now'}
              </CodeBlockRunButton>

              {error && (
                <ErrorsBadge
                  title='Run has 1 error'
                  errors={[error]}
                  usePortal
                />
              )}
            </HStack>
          ) : (
            <LoginToRun>
              <a href={loginLink(window.location.href)}>log in</a> to run this
              example
            </LoginToRun>
          )}
        </CodeBlockElement.Section>
      )}

      {!error && output && showOutput && (
        <CodeBlockElement.Output title={'Output'} output={output} />
      )}
    </CodeBlockElement.Root>
  )
}

export function codeBlocksToCodeExamples(
  codeBlocks: CodeBlockType,
): CodeExampleType[] {
  // FIXME: strictNullCheck temporary fix
  // @ts-expect-error TS(2322): Type '{ language: CodeHighlighterLanguages; title:... Remove this comment to see the full error message
  return (Object.keys(codeBlocks) as CodeBlockExtension[]).map((ext) => ({
    language: mapExtensionToLanguage(ext),
    title: mapExtensionToLanguageTitle(ext),
    code: codeBlocks[ext],
  }))
}

function mapExtensionToLanguageTitle(ext: CodeBlockExtension) {
  // FIXME: strictNullCheck temporary fix
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  return getCodeBlockExtension(ext).title
}
function mapExtensionToLanguage(ext: CodeBlockExtension) {
  // FIXME: strictNullCheck temporary fix
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  return getCodeBlockExtension(ext).language
}
function getCodeBlockExtension(ext: CodeBlockExtension) {
  return CodeBlockExtensions.get(ext) || CodeBlockExtensions.get('js')
}

export function renderVariables(
  code: string,
  variables: Record<string, any>,
  parametersSpec?: ParametersSpec,
) {
  let result = code

  const defaultValues = {}
  for (const [key, value] of Object.entries(parametersSpec ?? {})) {
    if (typeof value === 'object' && value?.default) {
      defaultValues[key] = value.default
    }
  }

  const values = {
    ...defaultValues,
    ...(variables ?? {}),
  }

  for (const key of Object.keys(parametersSpec ?? {})) {
    let value = values?.[key]
    const parameterSpec = parametersSpec?.[key]
    const type =
      typeof parameterSpec === 'object' ? parameterSpec?.type : parameterSpec
    const defaultValue =
      typeof parameterSpec === 'object' ? parameterSpec?.default : undefined

    // For objects and booleans, we remove the outer quotes
    // that we add to make placeholders valid JSON (like '{OBJECT}' or '{BOOLEAN}')
    // For other types, we keep the quotes.
    const isStringType =
      type &&
      ![
        CodeParamType.Object,
        CodeParamType.Boolean,
        CodeParamType.DataSchema,
      ].includes(type)

    // Some keys can have format `baseKey/subKey` to store different values for different objects
    // for example, `INPUT/<objectId1>` and `INPUT/<objectId2>`.
    const baseKey = key.split('/')[0]

    const regexp = isStringType
      ? new RegExp(`{${baseKey}}`, 'g')
      : new RegExp(`['"]?{${baseKey}}['"]?`, 'g')

    if (value === undefined) {
      value = defaultValue
    }

    let stringValue = value === undefined ? '' : JSON.stringify(value, null, 2)

    if (isStringType) {
      stringValue = stringValue.replace(/"/g, '')
    }

    if (stringValue) {
      result = result.replace(regexp, stringValue)
    }
  }

  return result
}

ExampleCodeBlock.ParamType = CodeParamType

export { CodeParamType }
